import { Component, inject, OnInit, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { FindNotificationsResponseDto } from '@orderna/admin-panel/src/app/services/notifications/dtos/find-notifications.response.dto';
import moment from 'moment';
import { dropdownFadeInOutAnimation } from '@orderna/admin-panel/src/utils/animations';
import {
  ButtonComponent,
  ButtonTextDirective,
} from '@orderna/admin-panel/src/app/shared/components/button/button.component';
import { HttpErrorResponse } from '@angular/common/http';
import { NotificationsApiService } from '@orderna/admin-panel/src/app/services/notifications/notifications-api.service';
import { Metadata } from '@orderna/admin-panel/src/app/model/metadata';
import { openErrorModal } from '@orderna/admin-panel/src/app/shared/components/modal/error-dialog/error-dialog.component';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { SnackbarService } from '@orderna/admin-panel/src/app/shared/services/snackbar.service';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { PANEL_CLOSE_FN } from '../notification-panel-trigger/notification-panel-trigger.component';

@Component({
  selector: 'app-notification-panel',
  standalone: true,
  imports: [
    CommonModule,

    MatIconModule,
    MatProgressSpinnerModule,

    ButtonComponent,
    ButtonTextDirective,
  ],
  templateUrl: './notification-panel.component.html',
  animations: [dropdownFadeInOutAnimation],
})
export class NotificationPanelComponent implements OnInit {
  #notificationsService = inject(NotificationsApiService);
  #dialog = inject(MatDialog);
  #router = inject(Router);
  #snackbarService = inject(SnackbarService);
  #panelCloseFn = inject(PANEL_CLOSE_FN);

  notifications = signal<FindNotificationsResponseDto['data']>([]);
  meta = signal<Metadata>({ total: 0 });
  markingAllAsRead = signal<boolean>(false);
  loading = signal(false);

  async ngOnInit() {
    await this.loadNotifications();
  }

  async loadNotifications() {
    try {
      this.loading.set(true);
      const { meta, data: notifications } =
        await this.#notificationsService.findNotifications({ isRead: false });

      this.notifications.set(notifications);
      this.meta.set(meta);
    } catch (error) {
      this.onError(error as HttpErrorResponse);
    } finally {
      this.loading.set(false);
    }
  }

  closePanel() {
    this.#panelCloseFn();
  }

  async goToNotificationPage(notificationId: number) {
    this.closePanel();
    return this.#router.navigate(['notifications', notificationId]);
  }

  async goToNotificationsPage() {
    this.closePanel();
    return this.#router.navigate(['notifications']);
  }

  async onMarkAllAsRead() {
    try {
      this.markingAllAsRead.set(true);

      const { updated } =
        await this.#notificationsService.markAllNotificationsAsRead();
      if (updated === 0) {
        this.#snackbarService.showDefault(
          'all notifications are already read.'
        );
        return;
      }

      const now = moment().toISOString();

      this.notifications.update((notifications) =>
        notifications.map((n) => ({
          ...n,
          readAt: now,
        }))
      );

      this.#snackbarService.showSuccess('notifications marked as read!');
    } catch (error) {
      this.onError(error as HttpErrorResponse);
    } finally {
      this.markingAllAsRead.set(false);
    }
  }

  protected formatNotificationDate(dateStr: string) {
    const m = moment(dateStr);
    if (moment().diff(m, 'days') === 0) {
      // Today → show relative minutes
      return m.fromNow();
    } else {
      // Older → calendar
      return m.calendar(null, {
        lastDay: '[Yesterday]',
        lastWeek: 'MMM D, YYYY',
        sameElse: 'MMM D, YYYY',
      });
    }
  }

  private onError(response: HttpErrorResponse) {
    console.error(response);
    openErrorModal(
      this.#dialog,
      response.error?.message || 'An unexpected error occurred.'
    );
  }
}
